import { DocumentationLayout, type DocumentationHeading, type DocumentationNavGroup } from "@sylviq/ui-react-web/components/docs/DocumentationLayout";
import { MonochromeMark } from "@sylviq/ui-react-web/components/ui/MonochromeMark";
import { type ReactNode } from "react";

export function DocsShell({
  navigation,
  activeHref,
  headings,
  children,
}: {
  navigation: readonly DocumentationNavGroup[];
  activeHref: string;
  headings?: readonly DocumentationHeading[];
  children: ReactNode;
}) {
  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:rounded-lg focus:bg-[var(--surface-solid)] focus:px-4 focus:py-2 focus:text-sm focus:font-medium"
      >
        Skip to documentation
      </a>
      <header className="sticky top-0 z-30 border-b border-[var(--border)] bg-[var(--surface)]">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
          <a
            href="/docs"
            aria-label="SYLVIQ Documentation home"
            className="flex shrink-0 items-center gap-2 rounded-lg px-1 py-1"
          >
            <MonochromeMark
              src="/brand/sylviq-mark-3.svg"
              className="h-9 w-9"
              label={null}
            />
            <span className="text-sm font-semibold tracking-[0.12em] text-[var(--foreground)]">
              SYLVIQ
            </span>
            <span className="hidden text-sm text-[var(--muted-foreground)] sm:inline">
              Docs
            </span>
          </a>
          <nav
            aria-label="Documentation site"
            className="flex items-center gap-3 text-sm sm:gap-5"
          >
            <a className="hidden rounded px-1 py-2 text-[var(--muted-foreground)] hover:text-[var(--foreground)] sm:inline" href="https://www.sylviq.io/">SYLVIQ</a>
            <a className="hidden rounded px-1 py-2 text-[var(--muted-foreground)] hover:text-[var(--foreground)] sm:inline" href="https://www.sylviq.io/pricing">Pricing</a>
            <a className="rounded px-1 py-2 text-[var(--muted-foreground)] hover:text-[var(--foreground)]" href="https://www.sylviq.io/support">Support</a>
            <a className="rounded px-1 py-2 text-[var(--muted-foreground)] hover:text-[var(--foreground)]" href="https://www.sylviq.io/login">Sign in</a>
          </nav>
        </div>
      </header>
      <main id="main" className="relative">
        <section className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
          <DocumentationLayout
            navigation={navigation}
            activeHref={activeHref}
            headings={headings}
          >
            {children}
          </DocumentationLayout>
        </section>
      </main>
      <footer className="border-t border-[var(--border)] bg-[var(--surface)]">
        <div className="mx-auto flex max-w-7xl flex-col gap-2 px-4 py-6 text-xs text-[var(--muted-foreground)] sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <span>© {new Date().getFullYear()} SYLVIQ. All rights reserved.</span>
          <div className="flex flex-wrap gap-4">
            <a className="hover:text-[var(--foreground)]" href="https://www.sylviq.io/legal/privacy">Privacy</a>
            <a className="hover:text-[var(--foreground)]" href="https://www.sylviq.io/legal/terms">Terms</a>
            <a className="hover:text-[var(--foreground)]" href="https://www.sylviq.io/support">Contact support</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
